import React, { useEffect, useState, useContext, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { generatePublishedUrl } from '../../core/utils/urlUtils';
import TextItem from "../components/Text";
import useContainerStyle from "../../core/hooks/useContainerStyle";
import useBaseStyle from "../../core/hooks/useBaseStyle";
import useSpacing from "../../core/hooks/useSpacing";
import useBackground from "../../core/hooks/useBackground";
import Background from "../components/Background";
import ContainerEffectOverlay from "../components/ContainerEffectOverlay";
import EditableElement from "../../editor/components/EditableElement";
import { EditContext } from "../../editor/context/EditContext";
import { apiFetch } from "../../core/utils/api";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { DragHandleProvider } from "../../editor/context/DragHandleContext";
import { makeSortableId } from "../../editor/utils/reorderCache";
import { reorderSidebarSections } from "../../editor/utils/reorderMove";
import AddButtonWithIndicator from "../../editor/components/AddButtonWithIndicator";
import AddButtonOverlay from "../../editor/add-buttons/AddButtonOverlay";
import { usePreviewOptional } from "../../editor/context/PreviewContext";
import { useEditorInteractionOptional } from "../../editor/context/EditorInteractionContext";

const SideBarSectionLink = React.memo(function SideBarSectionLink({
  section,
  targetUrl,
  isExternal,
  onNavigatePreview,
  isEditMode,
}) {
  const editContext = useContext(EditContext);
  const { styleClasses: baseClass, inlineStyles: baseStyle } = useBaseStyle(section);
  const containerClasses = useContainerStyle(section);
  const { padding: paddingClass } = useSpacing(section, false, null, null, true, false);
  const {
    bgImageUrl: sectionBgImageUrl,
    imageStyleClasses: sectionImageStyleClasses,
    imageInlineStyles: sectionImageInlineStyles,
    bgPatternClass: sectionBgPatternClass = "",
    bgPatternStyle: sectionBgPatternStyle = {},
    bgPatternJsLayer: sectionBgPatternJsLayer = null,
  } = useBackground(section?.background || {});

  return (
    <Link
      to={targetUrl.startsWith("http") ? { pathname: targetUrl } : targetUrl}
      className={[
        "group/sidebar-section relative overflow-hidden",
        "block w-full",
        "hover:text-blue-500 transition-colors",
        paddingClass,
        containerClasses,
        baseClass,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ ...baseStyle }}
      onClick={(e) => {
        if (onNavigatePreview && !isExternal) {
          e.preventDefault();
          onNavigatePreview(targetUrl);
          return;
        }
        if (editContext || isEditMode) {
          e.preventDefault();
        }
      }}
      {...(isExternal && { target: "_blank", rel: "noopener noreferrer" })}
    >
      <Background
        bgImage={sectionBgImageUrl}
        imageStyleClasses={sectionImageStyleClasses}
        imageInlineStyles={sectionImageInlineStyles}
        colorClass={sectionBgPatternClass}
        colorStyle={sectionBgPatternStyle}
        jsPatternLayer={sectionBgPatternJsLayer}
      />
      <div
        className="relative z-10 inline-block max-w-full"
        data-sidebar-section-measure="true"
      >
        <TextItem item={section.label} returnDiv={false} />
      </div>
    </Link>
  );
});
SideBarSectionLink.displayName = "SideBarSectionLink";

const SortableSectionWrapper = React.memo(function SortableSectionWrapper({ id, data, children }) {
  // dnd-kit treats `data` as an input to its internal registration effects.
  // Passing a new object each render can cause re-registration churn and, in some cases,
  // "Maximum update depth exceeded" loops. Stabilize by memoizing by value.
  const stableData = useMemo(() => data, [JSON.stringify(data ?? null)]);
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id,
    data: stableData,
  });
  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <DragHandleProvider value={{ dragListeners: listeners }}>
        {children}
      </DragHandleProvider>
    </div>
  );
});
SortableSectionWrapper.displayName = "SortableSectionWrapper";

const SIDEBAR_MIN_WIDTH_PX = 225;
const SIDEBAR_HORIZONTAL_CHROME_PX = 32; // Matches sidebar `p-4` horizontal padding.

const SideBar = ({
  websiteId,
  username,
  domain,
  side,
  onStickyChange = null,
  onStickyWidthChange = null,
}) => {
  const editContext = useContext(EditContext);
  const preview = usePreviewOptional();
  const editorInteraction = useEditorInteractionOptional();
  const [sidebarData, setSidebarData] = useState(null);
  const sectionsListRef = useRef(null);

  const fetchSidebar = useCallback(async () => {
    try {
      const endpoint =
        side === "left"
          ? `/api/websites/${websiteId}/leftsidebar/`
          : `/api/websites/${websiteId}/rightsidebar/`;
      const res = await apiFetch(endpoint);
      if (res.ok) {
        const data = await res.json();
        setSidebarData(data);
      } else {
        console.error(`Error fetching ${side} sidebar`);
      }
    } catch (error) {
      console.error("Network error:", error);
    }
  }, [websiteId, side]);

  useEffect(() => {
    fetchSidebar();
  }, [fetchSidebar]);

  // Refresh on editor-driven invalidations (AI reload_targets dispatch this event).
  useEffect(() => {
    const handler = (evt) => {
      try {
        const detail = evt?.detail || {};
        // If the event is scoped to a websiteId and it's not ours, ignore it.
        if (detail.websiteId && String(detail.websiteId) !== String(websiteId)) {
          return;
        }
      } catch (_) {
        // Ignore malformed event detail; we'll still refetch.
      }
      const run = () => fetchSidebar();
      if (typeof editorInteraction?.runWhenIdle === "function") {
        editorInteraction.runWhenIdle(run);
      } else {
        run();
      }
    };

    window.addEventListener("editor:refresh-website", handler);
    return () => window.removeEventListener("editor:refresh-website", handler);
  }, [fetchSidebar, websiteId, editorInteraction]);

  // Targeted refresh: sidebar autosaves should refresh ONLY the sidebar, not the entire editor/website.
  useEffect(() => {
    const handler = (evt) => {
      const detail = evt?.detail || {};
      try {
        if (detail.websiteId && String(detail.websiteId) !== String(websiteId)) return;
        if (detail.side && String(detail.side).toLowerCase() !== String(side).toLowerCase()) return;
      } catch (_) {
        // ignore detail parse; best-effort refetch
      }
      const run = () => fetchSidebar();
      if (typeof editorInteraction?.runWhenIdle === "function") {
        editorInteraction.runWhenIdle(run);
      } else {
        run();
      }
    };

    window.addEventListener("editor:refresh-sidebar", handler);
    return () => window.removeEventListener("editor:refresh-sidebar", handler);
  }, [fetchSidebar, websiteId, side, editorInteraction]);

  // NOTE: keep hooks order consistent; render guard below

  // Edit mode detection
  const isEditMode = editContext?.selectedElement !== undefined;

  // Keep UI reactive while editing the sidebar (optimistic updates live in EditContext.selectedElement)
  const currentSidebar = useMemo(() => {
    const sel = editContext?.selectedElement;
    if (!sel || sel.type !== "sidebar" || String(sel.id) !== String(sidebarData?.id)) {
      return sidebarData;
    }

    const deepMerge = (target, source) => {
      if (!target) return source;
      if (!source) return target;
      const result = Array.isArray(target) ? [...target] : { ...target };
      for (const key in source) {
        const sv = source[key];
        if (sv && typeof sv === "object" && !Array.isArray(sv)) {
          result[key] = deepMerge(target[key] || {}, sv);
        } else {
          result[key] = sv;
        }
      }
      return result;
    };

    return deepMerge(sidebarData, sel);
  }, [editContext?.selectedElement, sidebarData]);

  const isSticky = Boolean(currentSidebar?.sticky);

  useEffect(() => {
    if (typeof onStickyChange !== "function") return;
    onStickyChange(isSticky);
  }, [onStickyChange, isSticky]);

  useEffect(() => {
    if (typeof onStickyWidthChange !== "function") return;

    const runMeasurement = () => {
      const listNode = sectionsListRef.current;
      if (!listNode) {
        onStickyWidthChange(SIDEBAR_MIN_WIDTH_PX);
        return;
      }

      const sectionNodes = listNode.querySelectorAll('[data-sidebar-section-measure="true"]');
      let longestSectionWidth = 0;
      sectionNodes.forEach((node) => {
        const rectWidth = Math.ceil(node.getBoundingClientRect?.().width || 0);
        const scrollWidth = Math.ceil(node.scrollWidth || 0);
        longestSectionWidth = Math.max(longestSectionWidth, rectWidth, scrollWidth);
      });

      const desiredWidth = Math.max(
        SIDEBAR_MIN_WIDTH_PX,
        longestSectionWidth + SIDEBAR_HORIZONTAL_CHROME_PX,
      );
      onStickyWidthChange(desiredWidth);
    };

    const rafId = window.requestAnimationFrame(runMeasurement);
    return () => window.cancelAnimationFrame(rafId);
  }, [
    onStickyWidthChange,
    currentSidebar?.id,
    currentSidebar?.sections,
    currentSidebar?.sections?.length,
  ]);

  // After autosave completes for sidebar edits, refetch so preview stays correct even after deselecting.
  const selectedElementType = editContext?.selectedElement?.type;
  const saveStatus = editContext?.saveStatus;
  useEffect(() => {
    if (!isEditMode) return;
    if (saveStatus !== "saved") return;
    if (selectedElementType !== "sidebar" && selectedElementType !== "sidebar_section") return;
    fetchSidebar();
  }, [isEditMode, saveStatus, selectedElementType, fetchSidebar]);

  // Background image/pattern support (use currentSidebar so edits reflect immediately)
  // NOTE: useBackground supports both legacy {background_image} and current {background_image_url}.
  const {
    bgImageUrl,
    imageStyleClasses,
    imageInlineStyles,
    bgPatternClass = "",
    bgPatternStyle = {},
    bgPatternJsLayer = null,
  } = useBackground(currentSidebar?.background || {});

  const { styleClasses: sidebarBaseStyleClasses, inlineStyles: sidebarBaseInlineStyles } = useBaseStyle(currentSidebar);

  // Direct sidebar section creation handler
  const handleAddSection = useCallback(
    async (order) => {
      if (!sidebarData?.id) {
        console.error('[SideBar] No sidebar ID available');
        return;
      }

      try {
        // Create a new sidebar section via API
        const endpoint =
          side === "left"
            ? `/api/websites/${sidebarData.id}/leftsidebar/sections/create/`
            : `/api/websites/${sidebarData.id}/rightsidebar/sections/create/`;

        const response = await apiFetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            order: order,
            label: { content: 'New Section', type: 'text' },
            link_choice: 'internal',
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create sidebar section');
        }

        // Refresh the sidebar data
        await fetchSidebar();
      } catch (err) {
        console.error('[SideBar] Failed to add section:', err);
        alert(`Failed to add sidebar section: ${err.message}`);
      }
    },
    [sidebarData, side, fetchSidebar],
  );

  // Keep SortableContext `items` stable when section IDs/order are unchanged.
  // This avoids dnd-kit churn that can cause "Maximum update depth exceeded" loops.
  const sortableSectionSignature = (currentSidebar?.sections || [])
    .map((s) => s?.id)
    .join("|");
  const sortableSectionIds = useMemo(
    () =>
      (currentSidebar?.sections || []).map((s) =>
        makeSortableId({ type: "sidebar_section", id: s.id }),
      ),
    [sortableSectionSignature],
  );

  if (!currentSidebar) return null;

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const indexOf = (id) => sidebarData.sections.findIndex((s) => makeSortableId({ type: "sidebar_section", id: s.id }) === id);
    const fromIndex = indexOf(String(active.id));
    const toIndex = indexOf(String(over.id));
    if (fromIndex === -1 || toIndex === -1) return;
    const next = sidebarData.sections.slice();
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setSidebarData({ ...sidebarData, sections: next });
    try {
      const payload = next.map((s, idx) => ({ id: s.id, order: idx }));
      await reorderSidebarSections({ sidebarId: sidebarData.id, side: sidebarData.side, sections: payload });
    } catch (_) {
      try {
        const endpoint = sidebarData.side === "left" ? `/api/websites/${websiteId}/leftsidebar/` : `/api/websites/${websiteId}/rightsidebar/`;
        const res = await apiFetch(endpoint);
        if (res.ok) setSidebarData(await res.json());
      } catch {}
    }
  };

  const sidebarContent = (
    <div className={`sidebar sidebar-${side} p-4 relative h-full min-h-full ${sidebarBaseStyleClasses}`.trim()} style={sidebarBaseInlineStyles}>
      {/* background layer */}
      <Background
        bgImage={bgImageUrl}
        imageStyleClasses={imageStyleClasses}
        imageInlineStyles={imageInlineStyles}
        colorClass={bgPatternClass}
        colorStyle={bgPatternStyle}
        jsPatternLayer={bgPatternJsLayer}
      />
      <ContainerEffectOverlay data={currentSidebar} />
      {/* content layer */}
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortableSectionIds} strategy={verticalListSortingStrategy}>
          <ul ref={sectionsListRef} className="relative z-10">
            {(currentSidebar.sections || []).map((section, idx) => {
          let targetUrl = "#";
          if (section.link_choice === "internal" && section.link_internal) {
            targetUrl = generatePublishedUrl(username, domain, section.link_internal.subdomain || "");
          } else if (section.link_choice === "external" && section.link_external) {
            targetUrl = section.link_external;
          }
          const isExternal = section.link_choice === "external";
          const handlePreviewNavigate = preview?.onNavigate;

          const sectionContent = (
            <li
              key={section.id}
              className="mb-4 relative group w-full"
            >
              <SideBarSectionLink
                section={section}
                targetUrl={targetUrl}
                isExternal={isExternal}
                onNavigatePreview={handlePreviewNavigate}
                isEditMode={isEditMode}
              />
              
              {/* Add button before first section (shown on hover in edit mode) */}
              {isEditMode && idx === 0 && (
                <AddButtonOverlay
                  layer="sidebar-section-before"
                  overlayId={`sidebar-section-before-${sidebarData.id}-${section.id}`}
                  orientation="horizontal"
                  placement="before"
                  className="transition-opacity opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto"
                >
                  <AddButtonWithIndicator
                    small
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddSection(0);
                    }}
                  />
                </AddButtonOverlay>
              )}
              
              {/* Add button after this section (shown on hover in edit mode) */}
              {isEditMode && (
                <AddButtonOverlay
                  layer="sidebar-section-after"
                  overlayId={`sidebar-section-after-${sidebarData.id}-${section.id}-${idx}`}
                  orientation="horizontal"
                  placement="after"
                  className="transition-opacity opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto"
                >
                  <AddButtonWithIndicator
                    small
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddSection(idx + 1);
                    }}
                  />
                </AddButtonOverlay>
              )}
            </li>
          );

            return (
              <SortableSectionWrapper
                key={makeSortableId({ type: "sidebar_section", id: section.id })}
                id={makeSortableId({ type: "sidebar_section", id: section.id })}
                data={{ parentType: "sidebar", parentId: currentSidebar.id, kind: "sidebar_section", index: idx }}
              >
                <EditableElement
                  element={{
                    ...section,
                    type: "sidebar_section",
                    website: currentSidebar.website,
                    // IMPORTANT: keep `side` at the top-level so EditContext can route autosave
                    // correctly even after the server response replaces `sidebar` with a numeric id.
                    side: currentSidebar.side,
                    sidebar: currentSidebar,
                  }}
                >
                  {sectionContent}
                </EditableElement>
              </SortableSectionWrapper>
            );
          })}
            
            {/* Add button for empty sidebar (shown in edit mode) */}
            {isEditMode && (currentSidebar.sections || []).length === 0 && (
              <AddButtonOverlay
                layer="sidebar-empty"
                overlayId={`sidebar-empty-${currentSidebar.id}`}
                strategy="inline"
                className="flex items-center justify-center py-8"
              >
                <AddButtonWithIndicator
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddSection(0);
                  }}
                />
              </AddButtonOverlay>
            )}
          </ul>
        </SortableContext>
      </DndContext>
    </div>
  );

  return (
    <div className="h-full">
      <EditableElement element={currentSidebar}>
        {sidebarContent}
      </EditableElement>
    </div>
  );
};

export default SideBar;
